// Copyright (C) 2021-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { useSelector } from 'react-redux';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';
import { Row, Col } from 'antd/lib/grid';
import moment from 'moment';
import { DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import Modal from 'antd/lib/modal';
import { CombinedState } from 'reducers';
import { Membership } from 'cvat-core-wrapper';
import { GetMembershipRolesChinese } from 'cvat-core/src/enums';

export interface Props {
    membershipInstance: Membership;
    onRemoveMembership(): void;
    onUpdateMembershipRole(role: string): void;
    onResendInvitation(key: string): void;
    onDeleteInvitation(key: string): void;
}

enum MenuKeys {
    RESEND_INVITATION = 'resend_invitation',
    DELETE_INVITATION = 'delete_invitation',
}

function MemberItem(props: Props): JSX.Element {
    const {
        membershipInstance, onRemoveMembership, onUpdateMembershipRole,
        onResendInvitation, onDeleteInvitation,
    } = props;
    const {
        user, joinedDate, role, invitation, isActive,
    } = membershipInstance;
    const { username, firstName, lastName } = user;
    const { username: selfUserName } = useSelector((state: CombinedState) => state.auth.user);

    const invitationActionsMenu = invitation && (
        <Dropdown overlay={(
            <Menu onClick={(action: MenuInfo) => {
                if (action.key === MenuKeys.RESEND_INVITATION) {
                    onResendInvitation(invitation.key);
                } else if (action.key === MenuKeys.DELETE_INVITATION) {
                    onDeleteInvitation(invitation.key);
                }
            }}
            >
                <Menu.Item key={MenuKeys.RESEND_INVITATION}>重发邀请</Menu.Item>
                <Menu.Divider />
                <Menu.Item key={MenuKeys.DELETE_INVITATION}>移除邀请</Menu.Item>
            </Menu>
        )}
        >
            <MoreOutlined className='cvat-organization-invintation-actions-button cvat-menu-icon' />
        </Dropdown>
    );

    const removeMemberBlock = (role === 'owner' || selfUserName === username) ? null : (
        <DeleteOutlined
            onClick={() => {
                Modal.confirm({
                    className: 'cvat-modal-organization-member-remove',
                    title: `您正在将用户 "${username}" 从该组织中移除`,
                    content: '当前用户将无权再访问组织数据。是否继续？',
                    okText: '确认',
                    cancelText: '取消',
                    okButtonProps: {
                        danger: true,
                    },
                    onOk: () => {
                        onRemoveMembership();
                    },
                });
            }}
        />
    );

    const leftBlock = isActive ? removeMemberBlock : invitationActionsMenu;

    return (
        <Row className='cvat-organization-member-item' justify='space-between'>
            <Col span={5} className='cvat-organization-member-item-username'>
                <Text strong>{username}</Text>
            </Col>
            <Col span={6} className='cvat-organization-member-item-name'>
                <Text strong>{`${firstName || ''} ${lastName || ''}`}</Text>
            </Col>
            <Col span={8} className='cvat-organization-member-item-dates'>
                {invitation ? (
                    <Text type='secondary'>
                        {`${moment(invitation.createdDate).locale('zh_cn').fromNow()} ${invitation.owner ? `由 ${invitation.owner.username} 邀请` : ''}`}
                    </Text>
                ) : null}
                {joinedDate ? <Text type='secondary'>{`${moment(joinedDate).locale('zh_cn').fromNow()} 加入`}</Text> : <Text type='secondary'>等待邀请中</Text>}
            </Col>
            <Col span={3} className='cvat-organization-member-item-role'>
                <Select
                    onChange={(_role: string) => {
                        onUpdateMembershipRole(_role);
                    }}
                    value={role}
                    disabled={role === 'owner'}
                >
                    {role === 'owner' ? (
                        <Select.Option value='owner'>{GetMembershipRolesChinese('owner')}</Select.Option>
                    ) : (
                        <>
                            <Select.Option value='worker'>{GetMembershipRolesChinese('worker')}</Select.Option>
                            <Select.Option value='supervisor'>{GetMembershipRolesChinese('supervisor')}</Select.Option>
                            <Select.Option value='maintainer'>{GetMembershipRolesChinese('maintainer')}</Select.Option>
                        </>
                    )}
                </Select>
            </Col>
            <Col span={1} className='cvat-organization-member-item-remove'>
                {leftBlock}
            </Col>
        </Row>
    );
}

export default React.memo(MemberItem);
